/**
 * Runtime configuration loader + provider for DYMAXION frontend apps.
 *
 * Pattern: build once, configure at runtime.
 *
 *   - The Docker image bakes a default `config.json` at
 *     /usr/share/nginx/html/config.json (vps-dev defaults).
 *   - Per-host deployment overrides this file via a Docker bind-mount,
 *     e.g.:
 *
 *       volumes:
 *         - /root/Dymaxion/dymaxion-frontend-v2/config-templates/tenant-config.dymaxion.json:/usr/share/nginx/html/config.json:ro
 *
 *     (VPS dev — reuses the existing dymaxion-frontend-v2 host config
 *      file so a single source of truth governs both apps.)
 *
 *   - On AWS spokes:
 *
 *       volumes:
 *         - /opt/archon/config/tenant-config.json:/usr/share/nginx/html/config.json:ro
 *
 *     (Gallantree + TPG — same per-host file already mounted by
 *      dymaxion-frontend-v2.)
 *
 *   - main.tsx fetches /config.json BEFORE the React tree mounts and
 *     wraps the app in a RuntimeConfigProvider so every component can
 *     read host-specific values via useRuntimeConfig().
 *
 * Schema is hand-validated (no Zod) to keep the runtime-dep surface
 * minimal. Adopts the dymaxion-frontend-v2 production schema verbatim
 * and extends it additively with a namespaced `dymaxionFrontend`
 * section so existing consumers are unaffected.
 *
 * Schema versioning is mandatory — bump $schemaVersion when making
 * breaking changes.
 */
import { createContext, createElement, useContext } from 'react';
// ── Validator (hand-rolled, no Zod) ─────────────────────────────────
class RuntimeConfigError extends Error {
    constructor(message) {
        super(`[runtime-config] ${message}`);
        this.name = 'RuntimeConfigError';
    }
}
function isString(v) {
    return typeof v === 'string';
}
function isBoolean(v) {
    return typeof v === 'boolean';
}
function isObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isStringArray(v) {
    return Array.isArray(v) && v.every(isString);
}
/**
 * Read an optional string field. Returns undefined if missing, null,
 * or empty. Throws if present but the wrong type. Used for the legacy
 * passthrough URL fields (executiveDashboardUrl, jiraProjectUrl, etc.)
 * which are declared optional in the schema and may or may not be
 * populated per host.
 */
function optionalString(obj, key, ctx) {
    const v = obj[key];
    if (v === undefined || v === null || v === '')
        return undefined;
    if (!isString(v)) {
        throw new RuntimeConfigError(`${ctx}.${key} must be a string when present (got ${JSON.stringify(v)})`);
    }
    return v;
}
function requireString(obj, key, ctx) {
    const val = obj[key];
    if (!isString(val) || val.length === 0) {
        throw new RuntimeConfigError(`${ctx}.${key} must be a non-empty string`);
    }
    return val;
}
function requireBoolean(obj, key, ctx) {
    const val = obj[key];
    if (!isBoolean(val)) {
        throw new RuntimeConfigError(`${ctx}.${key} must be a boolean`);
    }
    return val;
}
function requireObject(obj, key, ctx) {
    const val = obj[key];
    if (!isObject(val)) {
        throw new RuntimeConfigError(`${ctx}.${key} must be an object`);
    }
    return val;
}
function requireStringArray(obj, key, ctx) {
    const val = obj[key];
    if (!isStringArray(val)) {
        throw new RuntimeConfigError(`${ctx}.${key} must be an array of strings`);
    }
    return Object.freeze([...val]);
}
/**
 * Validate a fetched JSON blob against the RuntimeConfig schema. Throws
 * RuntimeConfigError on any structural problem with a path-prefixed
 * message so the failure is debuggable from devtools.
 */
export function validateRuntimeConfig(raw) {
    if (!isObject(raw)) {
        throw new RuntimeConfigError('root must be an object');
    }
    const schemaVersion = raw['$schemaVersion'];
    if (schemaVersion !== 1) {
        throw new RuntimeConfigError(`$schemaVersion must be 1 (got ${JSON.stringify(schemaVersion)})`);
    }
    const spokeName = requireString(raw, 'spokeName', 'config');
    const organizationName = requireString(raw, 'organizationName', 'config');
    const df = requireObject(raw, 'dymaxionFrontend', 'config');
    // brand
    const brandRaw = requireObject(df, 'brand', 'dymaxionFrontend');
    const brand = {
        wordmark: requireString(brandRaw, 'wordmark', 'dymaxionFrontend.brand'),
        preamble: requireString(brandRaw, 'preamble', 'dymaxionFrontend.brand'),
        tagline: requireString(brandRaw, 'tagline', 'dymaxionFrontend.brand'),
    };
    // auth
    const authRaw = requireObject(df, 'auth', 'dymaxionFrontend');
    const auth = {
        apiBase: requireString(authRaw, 'apiBase', 'dymaxionFrontend.auth'),
        defaultOrganization: requireString(authRaw, 'defaultOrganization', 'dymaxionFrontend.auth'),
        knownTenants: requireStringArray(authRaw, 'knownTenants', 'dymaxionFrontend.auth'),
        postLoginRedirect: requireString(authRaw, 'postLoginRedirect', 'dymaxionFrontend.auth'),
        deviceFingerprintingRequired: requireBoolean(authRaw, 'deviceFingerprintingRequired', 'dymaxionFrontend.auth'),
    };
    // hero
    const heroRaw = requireObject(df, 'hero', 'dymaxionFrontend');
    const hero = {
        splineScene: requireString(heroRaw, 'splineScene', 'dymaxionFrontend.hero'),
        fallbackBackground: requireString(heroRaw, 'fallbackBackground', 'dymaxionFrontend.hero'),
    };
    // links
    const linksRaw = requireObject(df, 'links', 'dymaxionFrontend');
    const links = {
        publicSite: requireString(linksRaw, 'publicSite', 'dymaxionFrontend.links'),
        privacyUrl: requireString(linksRaw, 'privacyUrl', 'dymaxionFrontend.links'),
        termsUrl: requireString(linksRaw, 'termsUrl', 'dymaxionFrontend.links'),
        contactUrl: requireString(linksRaw, 'contactUrl', 'dymaxionFrontend.links'),
    };
    // features
    const featuresRaw = requireObject(df, 'features', 'dymaxionFrontend');
    const features = {
        showTrustPillars: requireBoolean(featuresRaw, 'showTrustPillars', 'dymaxionFrontend.features'),
    };
    // Legacy passthrough URL fields — declared optional in the schema,
    // populated per-host in tenant-config.<host>.json. The portal SPA
    // reads these for tile click handlers (Executive Dashboard, JIRA,
    // Confluence, etc.). Without this passthrough they'd be present in
    // the JSON but stripped during validation, causing tiles to no-op.
    return {
        $schemaVersion: 1,
        spokeName,
        organizationName,
        monitoringUrl: optionalString(raw, 'monitoringUrl', 'config'),
        executiveDashboardUrl: optionalString(raw, 'executiveDashboardUrl', 'config'),
        jiraProjectUrl: optionalString(raw, 'jiraProjectUrl', 'config'),
        architectureDesignUrl: optionalString(raw, 'architectureDesignUrl', 'config'),
        qaTestingSuiteUrl: optionalString(raw, 'qaTestingSuiteUrl', 'config'),
        confluenceSpaceUrl: optionalString(raw, 'confluenceSpaceUrl', 'config'),
        controlPanelUrl: optionalString(raw, 'controlPanelUrl', 'config'),
        dymaxionFrontend: { brand, auth, hero, links, features },
    };
}
// ── Loader ──────────────────────────────────────────────────────────
const DEFAULT_CONFIG_URL = '/config.json';
/**
 * Fetch /config.json from the SPA's own nginx (where the per-host file
 * is bind-mounted) and validate it against the schema. Throws on fetch
 * or validation failure so main.tsx can render an obvious error UI
 * instead of booting with mystery defaults.
 */
export async function loadRuntimeConfig(url = DEFAULT_CONFIG_URL) {
    let response;
    try {
        response = await fetch(url, {
            cache: 'no-store',
            // SCRUM-728: must be 'same-origin' (not 'omit') so JWT cookies
            // travel with the config.json fetch on JWT-gated routes like
            // /dymaxion-landing-v2/. Login SPA at /login-v2/ is unauth-
            // friendly so the change is a no-op there. Both SPAs only ever
            // fetch their own same-origin /config.json, so 'same-origin'
            // is the strictest scope that still works behind an auth gate.
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        });
    }
    catch (err) {
        throw new RuntimeConfigError(`failed to fetch ${url}: ${err instanceof Error ? err.message : String(err)}`);
    }
    if (!response.ok) {
        throw new RuntimeConfigError(`fetch ${url} returned HTTP ${response.status}`);
    }
    let raw;
    try {
        raw = await response.json();
    }
    catch (err) {
        throw new RuntimeConfigError(`failed to parse ${url} as JSON: ${err instanceof Error ? err.message : String(err)}`);
    }
    return validateRuntimeConfig(raw);
}
// ── Provider + hook ─────────────────────────────────────────────────
const RuntimeConfigContext = createContext(null);
/**
 * Wraps the React tree and exposes the loaded runtime config via
 * useRuntimeConfig(). main.tsx is responsible for calling
 * loadRuntimeConfig() BEFORE rendering and passing the result here.
 */
export function RuntimeConfigProvider({ config, children, }) {
    return createElement(RuntimeConfigContext.Provider, { value: config }, children);
}
/**
 * Read the loaded runtime config from any descendant component. Throws
 * if called outside a RuntimeConfigProvider — this is intentional: it
 * means main.tsx forgot to wrap the app, which is a programming error
 * worth failing loudly on.
 */
export function useRuntimeConfig() {
    const config = useContext(RuntimeConfigContext);
    if (config === null) {
        throw new RuntimeConfigError('useRuntimeConfig() called outside RuntimeConfigProvider — wrap your app in main.tsx');
    }
    return config;
}
export { RuntimeConfigError };
//# sourceMappingURL=runtimeConfig.js.map